import type React from 'react';
import { Button, Typography, type TypographyProps } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { DashboardLanguageToggle } from '@/layout/components/DashboardLanguageToggle';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/** The step column's cap: 640 Accounting, 720 Branch, 960 Welcome, 1,120 Upload and Names, full for the table. */
export type WizardColumnWidth = 640 | 720 | 960 | 1120 | 'full';

// Literal class names, so Tailwind's scanner sees every one of them.
const COLUMN_WIDTH_CLASS: Record<WizardColumnWidth, string> = {
    640: 'max-w-[640px]',
    720: 'max-w-[720px]',
    960: 'max-w-[960px]',
    1120: 'max-w-[1120px]',
    full: 'max-w-none',
};

// The mark is a logo, not a heading.
const asSpan: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <span {...domProps}>{children}</span>;

type WizardFrameProps = {
    organisationName: string;
    isSigningOut: boolean;
    onSignOut: () => void;
    /** The step strip, rendered under the top bar. */
    strip: React.ReactNode;
    columnWidth: WizardColumnWidth;
    /**
     * What the current import is about (the chosen branch), shown at the top
     * of the column on every step after Branch; nothing when absent.
     */
    context?: React.ReactNode;
    children?: React.ReactNode;
};

/**
 * The onboarding wizard's full-screen frame (no sidebar, no navbar): a slim
 * top bar with the BOS mark, the organisation, the language switch and Sign
 * out; the step strip; then one centred column whose cap the step chooses,
 * headed by the import's context (the chosen branch) when there is one.
 */
export const WizardFrame: React.FC<WizardFrameProps> = ({
    organisationName,
    isSigningOut,
    onSignOut,
    strip,
    columnWidth,
    context,
    children,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex min-h-screen w-full flex-col bg-background"
            {...getNodeTestIdProp('onboarding-frame')}
        >
            <header
                className="flex items-center gap-3 border-b border-separator px-4 py-3 sm:px-6"
                {...getNodeTestIdProp('onboarding-top-bar')}
            >
                <Typography
                    render={asSpan}
                    type="h5"
                    weight="bold"
                    {...getNodeTestIdProp('onboarding-mark')}
                >
                    BOS
                </Typography>
                <Typography
                    className="min-w-0 flex-1"
                    color="muted"
                    truncate
                    type="body-sm"
                    {...getNodeTestIdProp('onboarding-organisation')}
                >
                    {organisationName}
                </Typography>
                <DashboardLanguageToggle
                    id="onboarding-language"
                    itemIdPrefix="onboarding"
                />
                <Button
                    isPending={isSigningOut}
                    onPress={onSignOut}
                    size="sm"
                    variant="tertiary"
                    {...getNodeTestIdProp('onboarding-sign-out')}
                >
                    {t('onboarding.frame.signOut')}
                </Button>
            </header>
            <div className="px-4 pt-4 sm:px-6">{strip}</div>
            <main
                className="flex w-full flex-1 justify-center px-4 py-6 sm:px-6"
                {...getNodeTestIdProp('onboarding-main')}
            >
                <div
                    className={`w-full ${COLUMN_WIDTH_CLASS[columnWidth]}`}
                    data-column-width={columnWidth}
                    {...getNodeTestIdProp('onboarding-column')}
                >
                    {context ? (
                        <div
                            className="pb-2"
                            {...getNodeTestIdProp('onboarding-context')}
                        >
                            {context}
                        </div>
                    ) : null}
                    {children}
                </div>
            </main>
        </div>
    );
};
